/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import * as React from 'react';
import { logError } from '../telemetry';

interface TelemetryErrorBoundaryProps {
    entryPoint?: string;
    children: React.ReactNode;
}

interface TelemetryErrorBoundaryState {
    hasError: boolean;
}

export class TelemetryErrorBoundary extends React.Component<TelemetryErrorBoundaryProps, TelemetryErrorBoundaryState> {
    constructor(props: TelemetryErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(): TelemetryErrorBoundaryState {
        return { hasError: true };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        const entryPoint = this.props.entryPoint ?? 'unknown';
        const componentStack = errorInfo.componentStack
            ? errorInfo.componentStack.split('\n').slice(0, 5).join('\n')
            : '';
        logError(
            'ReactRenderError',
            `[${entryPoint}] ${error.message}${componentStack ? '\nComponentStack: ' + componentStack : ''}`
        );
    }

    render() {
        if (this.state.hasError) {
            return null;
        }
        return this.props.children;
    }
}
